/**
 * PhotoUpload
 * Shows current photo (if any), lets user pick a new one, and optionally remove it.
 * Calls onUploaded(updatedPerson) after a successful upload/delete.
 */
import { useRef, useState } from 'react'
import { api, mediaUrl } from '../../services/api.js'
import './PhotoUpload.css'

export default function PhotoUpload({ person, onUploaded }) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [dragOver, setDragOver] = useState(false)

  const thumbUrl = person.photo_thumb_url || mediaUrl(person.photo_thumb_key)

  async function handleFile(file) {
    if (!file || !file.type.startsWith('image/')) {
      return setError('Please choose an image file.')
    }
    setUploading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('photo', file)
      const updated = await api.upload(`/persons/${person.id}/photo`, formData)
      onUploaded(updated)
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function removePhoto() {
    setUploading(true)
    setError(null)
    try {
      const updated = await api.delete(`/persons/${person.id}/photo`)
      onUploaded(updated)
    } catch (err) {
      setError(err.message || 'Failed to remove photo')
    } finally {
      setUploading(false)
    }
  }

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  return (
    <div className="photo-upload">
      <div
        className={`photo-upload__zone ${dragOver ? 'photo-upload__zone--over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt="Person photo"
            className="photo-upload__preview"
          />
        ) : (
          <div className="photo-upload__placeholder">
            <span className="photo-upload__icon">🖼</span>
            <span className="photo-upload__hint">
              {uploading ? 'Uploading…' : 'Click or drag to add a photo'}
            </span>
          </div>
        )}

        {uploading && (
          <div className="photo-upload__overlay">
            <span className="photo-upload__spinner">⏳</span>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0])}
      />

      <div className="photo-upload__actions">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {thumbUrl ? 'Change photo' : 'Upload photo'}
        </button>
        {thumbUrl && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ color: '#dc2626' }}
            onClick={removePhoto}
            disabled={uploading}
          >
            Remove
          </button>
        )}
      </div>

      {error && <div className="form-error" style={{ marginTop: '0.5rem' }}>{error}</div>}
    </div>
  )
}
